import {useEffect, useMemo} from "react";
import {Navigate, useLocation, useNavigate} from "react-router";
import {Route, Routes} from "react-router-dom";
import styled from "styled-components";
import {scheduleApi} from "../api/schedule-api";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {loadOldAlertAction} from "../app/reducers/alert-slice";
import {profileState} from "../app/reducers/profile-slice";
import {
    addSchedulePageAction,
    clearDialogScheduleAction,
    clearSelectedScheduleAction,
    scheduleState,
    setIsDashboardInitAction,
} from "../app/reducers/schedule-slice";
import {useGetAllAlertQuery} from "../query/alert-query";
import {socketIO} from "../services/socket-io";
import {WEB_SOCKET_URL} from "../types/constant";
import {LocalStorageKey} from "../types/local-storage/local-storage-key";
import {Pathname} from "../types/pathname";
import {SessionStorageKey} from "../types/session-storage/session-storage-key";
import useDidMountEffect from "../utils/custom-hook/use-did-mount-effect";
import {todayYYYYMMDD} from "../utils/date/date-format";
import {CompanyView} from "../views/CompanyView";
import DashboardView from "../views/DashboardView";
import DeliveryView from "../views/DeliveryView";
import LocationView from "../views/LocationView";
import ReportView from "../views/ReportView";
import {RoutingServiceView} from "../views/RoutingServiceView";
import {ScheduleCheck} from "../views/ScheduleCheck";
import {ScheduleFormView} from "../views/ScheduleFormView";
import {SettingView} from "../views/SettingView";
import {UserView} from "../views/UserView";
import Header from "./Header";
import {includesPermission, Permission} from "../utils/permission/permission";
import {isAtLeastAdmin} from "../types/user/user-type";
import {clearCachedUpdateDeliveries} from "../app/reducers/delivery-slice";

const SignInRouter = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const history = useNavigate();
    const {profile} = useAppSelector(profileState);
    useGetAllAlertQuery(!!profile);
    const {dialogSchedule, updateSchedule, selectedSchedule, isDashboardInit} =
        useAppSelector(scheduleState);

    const getSchedule = async () => {
        try {
            const todayFirstSchedulePageResponse = await scheduleApi.search({
                fromDate: todayYYYYMMDD,
                toDate: todayYYYYMMDD,
                page: 0,
            });

            const todayFirstSchedulePageDto = todayFirstSchedulePageResponse.data;
            dispatch(
                addSchedulePageAction({
                    schedulePageAllDto: todayFirstSchedulePageDto,
                })
            );

            if (todayFirstSchedulePageDto.totalPages !== 0) {
                for (let i = 1; i < todayFirstSchedulePageDto.totalPages; i++) {
                    const response = await scheduleApi.search({
                        fromDate: todayYYYYMMDD,
                        toDate: todayYYYYMMDD,
                        page: i,
                    });
                    const schedulePageResponse = response.data;
                    dispatch(
                        addSchedulePageAction({
                            schedulePageAllDto: schedulePageResponse,
                        })
                    );
                    if (todayFirstSchedulePageDto.totalPages === i + 1) {
                        dispatch(
                            setIsDashboardInitAction({
                                isDashboardInit: true,
                            })
                        );
                    }
                }
                if (todayFirstSchedulePageDto.totalPages === 1) {
                    dispatch(
                        setIsDashboardInitAction({
                            isDashboardInit: true,
                        })
                    );
                }
            } else {
                dispatch(
                    setIsDashboardInitAction({
                        isDashboardInit: true,
                    })
                );
            }
        } catch (error) {
            console.log(error);
        }
    };

    const token = useMemo(
        () =>
            sessionStorage.getItem(SessionStorageKey.AccessToken) ||
            localStorage.getItem(LocalStorageKey.AccessToken),
        []
    );
    const socket = useMemo(() => socketIO, []);

    useEffect(() => {
        getSchedule();
        dispatch(loadOldAlertAction());

        return () => {
            dispatch(setIsDashboardInitAction({isDashboardInit: false}));
        };
    }, []);

    useEffect(() => {
        if (token && isDashboardInit && !socket.is_connected()) {
            socket.connect(WEB_SOCKET_URL, token);
        }

        return () => {
            if (socket.is_connected()) {
                socket.close();
            }
        };
    }, [isDashboardInit, token]);

    useDidMountEffect(() => {
        if (dialogSchedule) {
            dispatch(clearDialogScheduleAction());
        }

        if (selectedSchedule) {
            dispatch(clearSelectedScheduleAction());
        }
    }, [location.pathname]);

    useDidMountEffect(() => {
        if (updateSchedule) {
            dispatch(clearCachedUpdateDeliveries());
            history(Pathname.ScheduleForm);
        }
    }, [updateSchedule]);

    return (
        <SignInRouterContainer>
            <Header/>
            <ContentContainer>
                <Routes>
                    <Route path={Pathname.Dashboard} element={<DashboardView/>}/>
                    <Route path={Pathname.ScheduleForm} element={<ScheduleFormView/>}/>
                    <Route path={Pathname.ScheduleCheck} element={<ScheduleCheck/>}/>
                    <Route path={Pathname.User} element={<UserView/>}/>
                    <Route path={Pathname.Company} element={<CompanyView/>}/>
                    <Route path={Pathname.DeliveryCreate} element={<DeliveryView/>}/>
                    <Route path={Pathname.Location} element={<LocationView/>}/>
                    {(profile?.company.name === "SUPERUSER" || includesPermission(profile?.company.roles ?? [], Permission.ACCESS_ROUTING_SERVICE)) && (
                        <Route
                            path={Pathname.RoutingService}
                            element={<RoutingServiceView/>}
                        />
                    )}
                    {(isAtLeastAdmin(profile?.userType)) && (
                        <>
                            <Route path={Pathname.Setting} element={<SettingView/>}/>
                        </>
                    )}
                    {(profile?.company.name === "SUPERUSER" || includesPermission(profile?.company.roles ?? [], Permission.ACCESS_SLA_REPORT)) && (
                        <Route path={Pathname.Report} element={<ReportView/>}/>
                    )}
                    <Route
                        path="*"
                        element={<Navigate replace to={Pathname.Dashboard}/>}
                    />
                </Routes>
            </ContentContainer>
        </SignInRouterContainer>
    );
};

export default SignInRouter;

const SignInRouterContainer = styled.div`
  display: flex;
  width: 100vw;
  height: 100vh;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  flex-grow: 1;
  width: 100%;
  // height: fit-content;
  overflow: scroll;
`;
